import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Transcript } from "@/types";
import { attachAudioEvents } from "@/audio-events";

const SPEEDS = [1, 1.25, 1.5, 2, 0.5] as const;

function findActiveSegment(segments: Transcript["segments"], time: number): number {
  let lo = 0;
  let hi = segments.length - 1;
  let result = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >>> 1;
    if (segments[mid].start <= time) {
      result = mid;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  return result;
}

export function useAudioPlayer(transcript?: Transcript) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [activeSegmentIndex, setActiveSegmentIndex] = useState(-1);

  const lastSegmentIndexRef = useRef(-1);
  const rafIdRef = useRef<number | null>(null);

  const isAudioFile =
    transcript?.type === "voice_recording" ||
    transcript?.type === "audio_or_video_import";

  const hasSegments = !!transcript?.segments?.length;

  const [isError, setIsError] = useState(false);
  // Length of the ORIGINAL file, which is what the element is pointed at —
  // so on a partial transcript this is the full media, not the transcribed
  // part. Null until metadata lands, and for durations a decoder can't report
  // (live streams surface Infinity).
  const [durationSeconds, setDurationSeconds] = useState<number | null>(null);

  useEffect(() => {
    setIsError(false);
    setDurationSeconds(null);
  }, [transcript?.originalFileUrl]);

  const hasAudio = isAudioFile && hasSegments && !isError;

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const segments = transcript?.segments;

    const onTimeUpdate = () => {
      const t = audio.currentTime;

      if (segments?.length) {
        const idx = findActiveSegment(segments, t);
        if (idx !== lastSegmentIndexRef.current) {
          lastSegmentIndexRef.current = idx;
          setActiveSegmentIndex(idx);
        }
      }

    };

    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const onEnded = () => {
      setIsPlaying(false);
      lastSegmentIndexRef.current = -1;
      setActiveSegmentIndex(-1);
    };
    const onError = () => setIsError(true);
    const onLoadedMetadata = () => {
      setIsError(false);
      const reported = audio.duration;
      setDurationSeconds(
        Number.isFinite(reported) && reported > 0 ? reported : null,
      );
    };

    return attachAudioEvents(audio, {
      timeupdate: onTimeUpdate,
      play: onPlay,
      pause: onPause,
      ended: onEnded,
      error: onError,
      loadedmetadata: onLoadedMetadata,
    });
  }, [hasAudio, transcript?.segments]);

  useEffect(() => {
    if (!isPlaying) {
      lastSegmentIndexRef.current = -1;
      setActiveSegmentIndex(-1);
    }
  }, [isPlaying]);

  useEffect(() => {
    if (audioRef.current) audioRef.current.playbackRate = playbackRate;
  }, [playbackRate]);

  const play = useCallback(() => {
    audioRef.current?.play().catch(() => {});
  }, []);

  const pause = useCallback(() => {
    audioRef.current?.pause();
  }, []);

  const togglePlayPause = useCallback(() => {
    if (isPlaying) {
      pause();
    } else {
      play();
    }
  }, [isPlaying, play, pause]);

  const playFromTime = useCallback((startTime: number) => {
    if (audioRef.current) {
      audioRef.current.currentTime = startTime;
      audioRef.current.play().catch(() => {});
    }
  }, []);

  const toggleSpeed = useCallback(() => {
    setPlaybackRate((prev) => {
      const idx = SPEEDS.indexOf(prev as (typeof SPEEDS)[number]);
      const next = SPEEDS[(idx + 1) % SPEEDS.length];
      if (audioRef.current) audioRef.current.playbackRate = next;
      return next;
    });
  }, []);

  return {
    audioRef,
    isPlaying,
    playbackRate,
    hasAudio,
    isAudioFile,
    durationSeconds,
    activeSegmentIndex,
    play,
    pause,
    togglePlayPause,
    playFromTime,
    toggleSpeed,
  };
}
